import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 700,
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  warningBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    backgroundColor: "#fffbeb",
    border: "1px solid #f59e0b",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    color: "#f59e0b",
    marginTop: 2,
    flexShrink: 0,
  },
  warningText: {
    fontSize: "0.8rem",
    color: "#92400e",
    lineHeight: 1.5,
  },
  chipsBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minHeight: 40,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  emptyText: {
    fontSize: "0.8rem",
    color: theme.palette.text.disabled,
    alignSelf: "center",
  },
  inputRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  textField: {
    flex: 1,
  },
  saveBtn: {
    marginTop: theme.spacing(2),
  },
}));

const DOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)+$/i;

const AgentDomains = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [domains, setDomains] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [loading, setLoading] = useState(false);

  // Carregar domínios atuais da empresa
  useEffect(() => {
    const load = async () => {
      if (!user?.companyId) return;
      try {
        const { data } = await api.get(`/companies/${user.companyId}`);
        if (Array.isArray(data?.agentAllowedDomains)) {
          setDomains(data.agentAllowedDomains);
        }
      } catch (err) {
        console.error("Erro ao carregar domínios:", err);
      }
    };
    load();
  }, [user]);

  const validateDomain = (value) => {
    const domain = value.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
    if (!domain) return { valid: false, error: "Informe um domínio.", domain };
    if (!DOMAIN_REGEX.test(domain)) {
      return { valid: false, error: "Formato inválido. Use apenas o hostname (ex: api.exemplo.com).", domain };
    }
    const blocked = [/^localhost$/i, /^127\./, /^0\.0\.0\.0$/, /^10\./, /^192\.168\./, /^169\.254\./, /^\*/];
    if (blocked.some((p) => p.test(domain))) {
      return { valid: false, error: "Domínio bloqueado por segurança.", domain };
    }
    if (domains.includes(domain)) {
      return { valid: false, error: "Domínio já adicionado.", domain };
    }
    return { valid: true, error: "", domain };
  };

  const handleAdd = () => {
    const { valid, error, domain } = validateDomain(inputValue);
    if (!valid) {
      setInputError(error);
      return;
    }
    setDomains((prev) => [...prev, domain]);
    setInputValue("");
    setInputError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (domain) => {
    setDomains((prev) => prev.filter((d) => d !== domain));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/companies/${user.companyId}/agent-domains`, {
        agentAllowedDomains: domains,
      });
      toast.success("Domínios autorizados salvos com sucesso.");
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao salvar domínios.";
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper} elevation={1}>
        <Typography variant="h6" className={classes.title}>
          Domínios Autorizados para Agentes IA
        </Typography>

        <Typography className={classes.subtitle}>
          A ferramenta <strong>fazer_requisicao_http</strong> dos Agentes IA só poderá enviar requisições para os domínios listados abaixo.
          Adicione apenas APIs de confiança da sua empresa.
        </Typography>

        <Box className={classes.warningBox}>
          <WarningIcon className={classes.warningIcon} fontSize="small" />
          <Typography className={classes.warningText}>
            <strong>Atenção:</strong> Adicione apenas domínios de APIs externas que você controla ou confia.
            Domínios locais, IPs privados e wildcards são bloqueados automaticamente.
            Qualquer tentativa de acesso a domínio não listado será registrada nos logs.
          </Typography>
        </Box>

        {/* Lista de chips */}
        <Box className={classes.chipsBox}>
          {domains.length === 0 ? (
            <span className={classes.emptyText}>Nenhum domínio autorizado. A ferramenta HTTP estará bloqueada.</span>
          ) : (
            domains.map((d) => (
              <Chip
                key={d}
                label={d}
                size="small"
                onDelete={() => handleDelete(d)}
                color="primary"
                variant="outlined"
              />
            ))
          )}
        </Box>

        {/* Input de adição */}
        <Box className={classes.inputRow}>
          <TextField
            className={classes.textField}
            variant="outlined"
            size="small"
            label="Adicionar domínio"
            placeholder="api.exemplo.com"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setInputError("");
            }}
            onKeyDown={handleKeyDown}
            error={!!inputError}
            helperText={inputError || "Apenas hostname, sem https:// ou /path"}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            style={{ height: 40, marginTop: 2 }}
          >
            Adicionar
          </Button>
        </Box>

        <Button
          className={classes.saveBtn}
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Salvando…" : "Salvar Domínios"}
        </Button>
      </Paper>
    </Box>
  );
};

export default AgentDomains;
